
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useIsActiveAffiliate } from './useIsActiveAffiliate';
import { AppSettingsService } from '@/services/app-settings-service';

/**
 * Hook to check if user has access to Market Analysis AI features
 * Access is granted to:
 * 1. Users who are active affiliates themselves - PRIORITY
 * 2. Users who were referred by an affiliate (customers of affiliates)
 * 3. Admin users (if admin override is enabled)
 */
export const useAffiliateMarketAccess = () => {
  const { user } = useAuth();
  
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('referred_by_affiliate, is_admin')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      
      console.log('🔍 User profile data:', {
        userId: user.id,
        referred_by_affiliate: data?.referred_by_affiliate,
        is_admin: data?.is_admin
      });
      
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: adminSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['marketAnalysisAdminSettings'],
    queryFn: async () => {
      const affiliateOnlyMode = await AppSettingsService.getMarketAnalysisAffiliateOnlyMode();
      const adminOverrideEnabled = await AppSettingsService.getMarketAnalysisAdminOverride();
      
      console.log('⚙️ Admin settings:', {
        affiliateOnlyMode,
        adminOverrideEnabled
      });
      
      return {
        affiliateOnlyMode,
        adminOverrideEnabled
      };
    },
  });

  // Check if user is admin
  const isAdmin = Boolean(
    user?.user_metadata?.role === 'admin' || 
    user?.user_metadata?.role === 'superadmin' ||
    userProfile?.is_admin
  );

  // Check if user was referred by an affiliate
  const wasReferred = Boolean(userProfile?.referred_by_affiliate);
  
  // Always check if user is active affiliate (higher priority than referral)
  const { isActiveAffiliate, hasAffiliateRecord } = useIsActiveAffiliate();

  // Check if user has market analysis access with NEW priority order
  const hasMarketAccess = Boolean(
    // If affiliate-only mode is disabled, everyone has access
    !adminSettings?.affiliateOnlyMode ||
    // Priority 1: User is an active affiliate (they can access without needing referral)
    isActiveAffiliate ||
    // Priority 2: User was referred by an affiliate (customers have access)
    wasReferred ||
    // Priority 3: Admin override (admin user can access if override is enabled)
    (isAdmin && adminSettings?.adminOverrideEnabled)
  );

  console.log('🎯 Market access logic (NEW PRIORITY):', {
    affiliateOnlyMode: adminSettings?.affiliateOnlyMode,
    isActiveAffiliate,
    wasReferred,
    isAdmin,
    adminOverrideEnabled: adminSettings?.adminOverrideEnabled,
    hasMarketAccess,
    priorityGranted: isActiveAffiliate ? 'AFFILIATE' : wasReferred ? 'REFERRED' : isAdmin && adminSettings?.adminOverrideEnabled ? 'ADMIN' : 'NONE'
  });

  return {
    hasMarketAccess,
    isActiveAffiliate,
    hasAffiliateRecord,
    wasReferred,
    isAdmin,
    affiliateOnlyMode: adminSettings?.affiliateOnlyMode || false,
    adminOverrideEnabled: adminSettings?.adminOverrideEnabled || false,
    isLoading: isLoadingProfile || isLoadingSettings,
  };
};
